import { generateAudioWsUrl } from "../ENV";
import { getUserToken } from "./Utils";

let currentWS = null;
let currentObjectUrl = null;
let currentGeneration = 0;

export function stopCurrentTTSStream() {
  currentGeneration += 1;
  if (currentWS) {
    try { currentWS.close(1000); } catch (e) {}
    currentWS = null;
  }
  if (currentObjectUrl) {
    URL.revokeObjectURL(currentObjectUrl);
    currentObjectUrl = null;
  }
}

export function streamSpeechWS(text, langCode) {
  if (
    !window.MediaSource ||
    !MediaSource.isTypeSupported("audio/mpeg")
  ) {
    return Promise.resolve(null);
  }

  currentGeneration += 1;
  const myGen = currentGeneration;

  // Abort any previous stream without incrementing generation again
  if (currentWS) {
    try { currentWS.close(1000); } catch (e) {}
    currentWS = null;
  }
  if (currentObjectUrl) {
    URL.revokeObjectURL(currentObjectUrl);
    currentObjectUrl = null;
  }

  return new Promise((resolve) => {
    const token = getUserToken();
    const base = generateAudioWsUrl.replace(/\/$/, "");
    const wsUrl = `${base}?token=${encodeURIComponent(token)}&lang_code=${encodeURIComponent(langCode)}`;

    const ws = new WebSocket(wsUrl);
    ws.binaryType = "arraybuffer";
    currentWS = ws;

    const mediaSource = new MediaSource();
    const objectUrl = URL.createObjectURL(mediaSource);
    currentObjectUrl = objectUrl;

    let sourceBuffer = null;
    const queue = [];
    let streamComplete = false;
    let resolved = false;

    function isStale() {
      return myGen !== currentGeneration;
    }

    function safeResolve(value) {
      if (!resolved) {
        resolved = true;
        resolve(value);
      }
    }

    function cleanup(revokeUrl = true) {
      if (currentWS === ws) currentWS = null;
      if (revokeUrl && currentObjectUrl === objectUrl) {
        URL.revokeObjectURL(objectUrl);
        currentObjectUrl = null;
      }
      try { ws.close(); } catch (e) {}
    }

    function processQueue() {
      if (isStale()) return;
      if (!sourceBuffer) return;

      if (queue.length > 0 && !sourceBuffer.updating) {
        try {
          sourceBuffer.appendBuffer(queue.shift());
        } catch (e) {
          // SourceBuffer may have been detached — ignore
        }
        return;
      }

      if (
        queue.length === 0 &&
        streamComplete &&
        !sourceBuffer.updating &&
        mediaSource.readyState === "open"
      ) {
        try {
          mediaSource.endOfStream();
        } catch (e) {}
      }
    }

    mediaSource.addEventListener("sourceopen", () => {
      if (isStale()) return;
      try {
        sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
        sourceBuffer.addEventListener("updateend", processQueue);
        // Drain anything queued before sourceopen fired
        processQueue();
      } catch (e) {
        cleanup();
        safeResolve(null);
      }
    });

    ws.onopen = () => {
      if (isStale()) { ws.close(1000); return; }
      ws.send(JSON.stringify({ type: "text", data: { text } }));
      // Do NOT resolve here — wait for first binary chunk (fix #2)
    };

    ws.onmessage = (event) => {
      if (isStale()) return;

      if (event.data instanceof ArrayBuffer) {
        // Resolve Promise on first binary frame so caller gets the URL
        // with data already available in the SourceBuffer (fix #2)
        if (!resolved) {
          safeResolve({ url: objectUrl });
        }
        if (sourceBuffer && !sourceBuffer.updating) {
          try {
            sourceBuffer.appendBuffer(event.data);
          } catch (e) {
            queue.push(event.data);
          }
        } else {
          queue.push(event.data);
        }
        return;
      }

      // Text frame
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "complete") {
          ws.close(1000);
          if (currentWS === ws) currentWS = null;
          streamComplete = true;
          processQueue();
          // If narration was disabled: backend sends complete with no binary frames
          if (!resolved) {
            safeResolve({ url: objectUrl });
          }
        } else if (msg.type === "error") {
          cleanup();
          safeResolve({ data: { detail: msg.message || "Audio synthesis failed" } });
        }
      } catch (e) {
        // Malformed text frame — ignore
      }
    };

    ws.onerror = () => {
      if (isStale()) return;
      cleanup();
      safeResolve(null); // REST fallback
    };

    ws.onclose = (event) => {
      if (isStale()) return;
      if (currentWS === ws) currentWS = null;

      if (event.code === 4401) {
        // Auth failure — do not fall back to REST (fix #4)
        if (currentObjectUrl === objectUrl) {
          URL.revokeObjectURL(objectUrl);
          currentObjectUrl = null;
        }
        safeResolve({ data: { detail: "Unauthorized" } });
        return;
      }

      if (event.code !== 1000) {
        // Abnormal close — fall back to REST
        if (currentObjectUrl === objectUrl) {
          URL.revokeObjectURL(objectUrl);
          currentObjectUrl = null;
        }
        safeResolve(null);
      }
      // code 1000: normal close, Promise already resolved
    };
  });
}
